/**
 * What a walkthrough looks like: the page dimmed except for the one thing she
 * is talking about, a cursor that travels to it, and a caption beside it with
 * the sentence and the controls.
 *
 * The overlay never takes the mouse. The person can still scroll, read and
 * click through it; only the caption card is pressable. Escape stops the
 * walkthrough, the arrow keys step through it and the space bar pauses it,
 * so it can be driven without the mouse as well.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { t } from "../../i18n";
import * as director from "./director";
import s from "./stage.module.css";

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;

/** The lit element's box, followed every frame so scrolling and resizing carry the light with it. */
function useBox(el: HTMLElement | null): Box | null {
  const [seen, setBox] = useState<{ el: HTMLElement; box: Box | null } | null>(null);
  useEffect(() => {
    if (!el) return;
    let raf = 0;
    let last = "";
    const tick = () => {
      if (el.isConnected) {
        const r = el.getBoundingClientRect();
        const key = `${r.top}|${r.left}|${r.width}|${r.height}`;
        if (key !== last) {
          last = key;
          setBox({ el, box: { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 } });
        }
      } else if (last !== "gone") {
        last = "gone";
        setBox({ el, box: null });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [el]);
  /* a box measured for an element no longer lit is not this one's */
  return el && seen?.el === el ? seen.box : null;
}

/** Beside the lit box when there is room — below, then above — otherwise along the bottom. */
function placeCaption(box: Box | null, card: { w: number; h: number }): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!box || vw < 640) return { left: 16, right: 16, bottom: 16, margin: "0 auto", maxWidth: 520 };
  const left = Math.max(16, Math.min(vw - card.w - 16, box.left));
  if (box.top + box.height + 14 + card.h < vh - 16) return { left, top: box.top + box.height + 14 };
  if (box.top - 14 - card.h > 16) return { left, top: box.top - 14 - card.h };
  /* a tall element: beside it, or failing that pinned to the bottom */
  if (box.left + box.width + 16 + card.w < vw) return { left: box.left + box.width + 16, top: Math.max(16, Math.min(vh - card.h - 16, box.top)) };
  return { left: 16, right: 16, bottom: 16, margin: "0 auto", maxWidth: 520 };
}

export function Stage() {
  const st = useSyncExternalStore(director.subscribe, director.snapshot, director.snapshot);
  const box = useBox(st.el);
  const cardRef = useRef<HTMLDivElement>(null);
  const [card, setCard] = useState({ w: 380, h: 170 });

  /* the caption's own size, so it can be placed beside the light without covering it */
  const live = !!st.script;
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      const r = e.target.getBoundingClientRect();
      setCard((c) => (Math.abs(r.width - c.w) > 2 || Math.abs(r.height - c.h) > 2 ? { w: r.width, h: r.height } : c));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [live]);

  useEffect(() => {
    if (!st.script) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && e.target.closest("input, textarea, select, [contenteditable='true']");
      if (e.key === "Escape") director.stop();
      else if (typing) return;
      else if (e.key === "ArrowRight") director.next();
      else if (e.key === "ArrowLeft") director.back();
      else if (e.key === " ") {
        e.preventDefault();
        director.togglePause();
      } else return;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [st.script]);

  if (!st.script) return null;
  const step = st.script.steps[st.index];
  const total = st.script.steps.length;

  return createPortal(
    <div className={s.stage} data-sky-stage="" aria-live="polite">
      {box ? (
        <div className={s.light} style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />
      ) : (
        <div className={s.dim} />
      )}

      {st.cursor && (
        <div className={`${s.cursor} ${st.cursor.pressing ? s.pressing : ""}`} style={{ transform: `translate(${st.cursor.x}px, ${st.cursor.y}px)` }} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26">
            <path d="M4 2l15 11-6.5 1.2L16 21l-3 1.4-3.4-6.8L4 20z" fill="#fff" stroke="#0b1f33" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          <span className={s.ripple} />
        </div>
      )}

      {st.ghost && box && (
        <div className={s.ghost} style={{ top: box.top + box.height / 2 - 14, left: box.left + box.width + 10 }}>
          {t("I would enter: {value}", { value: st.ghost })}
        </div>
      )}

      <div ref={cardRef} className={s.card} style={placeCaption(box, card)} role="dialog" aria-label={t(st.script.title)}>
        <div className={s.head}>
          <span className={s.badge}>{t("Sky")}</span>
          <span className={s.title}>{step.title ? t(step.title) : t(st.script.title)}</span>
          <span className={s.count}>{t("{i} of {n}", { i: st.index + 1, n: total })}</span>
        </div>
        <div className={s.text}>{step.text}</div>
        {st.note && <div className={s.note}>{t(st.note)}</div>}
        <div className={s.bar} aria-hidden="true">
          <span style={{ width: `${((st.index + 1) / total) * 100}%` }} />
        </div>
        <div className={s.controls}>
          <button type="button" onClick={director.back} disabled={st.index === 0} title={t("Previous step (←)")}>
            {t("Back")}
          </button>
          <button type="button" onClick={director.togglePause} title={t("Pause or resume (space)")}>
            {st.paused ? t("Resume") : t("Pause")}
          </button>
          <button type="button" onClick={director.toggleMute} title={st.muted ? t("Read the steps aloud") : t("Stop reading aloud")}>
            {st.muted ? t("Sound on") : t("Mute")}
          </button>
          <span className={s.spacer} />
          <button type="button" className={s.primary} onClick={director.next} title={t("Next step (→)")}>
            {st.index === total - 1 ? t("Finish") : t("Next")}
          </button>
          <button type="button" className={s.close} onClick={() => director.stop()} aria-label={t("End the walkthrough")} title={t("End the walkthrough (Esc)")}>
            ×
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
